/**
 * The assistant message a client shows while a turn is generated, built
 * from the "turn.stream" broadcasts of the backend (AiChatStreamBroadcaster):
 * batches of { type, ... } events on the chat's channel. The stored
 * transcript replaces it when the turn finishes.
 */
export const startStream = (chatId, invocationId) => ({
  chatId,
  invocationId,
  content: '',
  stepHasText: false,
  thinking: false,
  toolCalls: [],
  toolResults: [],
  error: null,
});

export const applyStreamEvents = (stream, events) =>
  (events || []).reduce(applyStreamEvent, stream);

function applyStreamEvent(stream, event) {
  switch (event.type) {
    case 'step_start':
      return { ...stream, stepHasText: false };
    case 'text': {
      // every step is its own utterance, joined like the stored transcript
      const separator = stream.content && !stream.stepHasText ? '\n\n' : '';
      return {
        ...stream,
        content: stream.content + separator + event.text,
        stepHasText: true,
      };
    }
    case 'reasoning_start':
      return { ...stream, thinking: true };
    case 'reasoning_end':
      return { ...stream, thinking: false };
    case 'tool_call':
      return {
        ...stream,
        toolCalls: [
          ...stream.toolCalls,
          {
            id: event.id,
            name: event.name,
            arguments: event.arguments || {},
            pending: false,
            visible: false,
          },
        ],
      };
    case 'tool_result':
      return {
        ...stream,
        toolResults: [
          ...stream.toolResults,
          {
            id: event.id,
            name: event.name,
            // the payload stays on the server, the transcript has it
            result: null,
            denied: !!event.denied,
            failed: !!event.failed,
            visible: false,
          },
        ],
      };
    case 'error':
      return { ...stream, error: event.message || 'error' };
    default:
      return stream;
  }
}

// The transient transcript message of the stream, null while there is
// nothing to show yet (the thinking indicator covers that).
export function streamingMessage(stream) {
  if (!stream || (!stream.content && stream.toolCalls.length === 0))
    return null;
  return {
    id: `stream-${stream.invocationId || stream.chatId}`,
    role: 'assistant',
    content: stream.content,
    status: 'streaming',
    streaming: true,
    interrupted: false,
    attachments: [],
    toolCalls: stream.toolCalls,
    toolResults: stream.toolResults,
  };
}
